import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import Logo from "@/components/Logo";
import CertificatePreview from "@/components/CertificatePreview";
import { Card } from "@/components/ui/card";
import { ShieldCheck, ShieldX, Download } from "lucide-react";

/** Public certificate verification page — the URL shared on LinkedIn. */
export default function CertificateVerify() {
  const { certId } = useParams();
  const [state, setState] = useState({ loading: true, cert: null, valid: false });

  useEffect(() => {
    axios.get(`${API}/program-certificate/verify/${certId}`)
      .then(({ data }) => setState({ loading: false, valid: !!data.valid, cert: data.valid ? data : null }))
      .catch(() => setState({ loading: false, valid: false, cert: null }));
  }, [certId]);

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <header className="bg-[#222222]">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="font-display font-extrabold text-white tracking-tight text-lg">
            UNI<span className="text-[#E53935]">V/</span>COUSTIC
          </div>
          <a href="https://www.univicoustic.com" target="_blank" rel="noreferrer" className="text-xs text-white/70 hover:text-[#E53935] transition-colors">
            www.univicoustic.com
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {state.loading ? (
          <Card className="p-12 text-center border-slate-200" data-testid="verify-loading">
            <div className="w-8 h-8 mx-auto border-2 border-[#E53935] border-t-transparent rounded-full animate-spin" />
            <div className="mt-3 text-sm text-slate-500">Verifying certificate…</div>
          </Card>
        ) : !state.valid ? (
          <Card className="p-12 text-center border-rose-200 bg-rose-50" data-testid="verify-invalid">
            <ShieldX size={40} className="mx-auto text-rose-400 mb-3" />
            <h1 className="text-xl font-display font-bold text-rose-900">Certificate not found</h1>
            <p className="text-sm text-rose-700 mt-2">
              We couldn't verify a certificate with ID <span className="font-mono">{certId}</span>.
            </p>
          </Card>
        ) : (
          <div data-testid="verify-valid">
            <div className="mb-8 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-full text-sm font-semibold" data-testid="verify-badge">
                <ShieldCheck size={16} /> Verified credential
              </div>
              <h1 className="mt-4 text-2xl md:text-3xl font-display font-bold text-[#222222]">
                {state.cert.full_name}
              </h1>
              <p className="mt-1 text-slate-600 text-sm max-w-xl mx-auto">
                has completed <strong>{state.cert.program_title}</strong> — a corporate L&amp;D program by the {state.cert.institution}.
              </p>
            </div>

            <CertificatePreview cert={state.cert} />

            <div className="mt-8 grid sm:grid-cols-3 gap-4 text-center">
              <Card className="p-4 border-slate-200">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Certificate ID</div>
                <div className="mt-1 font-mono font-semibold text-[#222222]" data-testid="verify-cert-id">{state.cert.cert_id}</div>
              </Card>
              <Card className="p-4 border-slate-200">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Issued</div>
                <div className="mt-1 font-semibold text-[#222222]">
                  {state.cert.issued_at ? new Date(state.cert.issued_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "—"}
                </div>
              </Card>
              <Card className="p-4 border-slate-200">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Institution</div>
                <div className="mt-1 font-semibold text-[#222222]">{state.cert.institution}</div>
              </Card>
            </div>

            <div className="mt-8 text-center">
              <a
                href={`${API}/program-certificate/${state.cert.cert_id}/pdf`}
                data-testid="verify-download-pdf"
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#222222] hover:bg-black text-white font-medium rounded-md transition-colors"
              >
                <Download size={16} /> Download PDF
              </a>
            </div>
          </div>
        )}
      </main>

      <footer className="border-t border-slate-200 mt-8">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between text-xs text-slate-500">
          <div className="inline-flex items-center gap-2">
            <Logo className="h-6" variant="mark" />
            <span>UniVicoustic Learning Academy · Corporate L&amp;D</span>
          </div>
          <div className="italic font-display">Makes sound sense.</div>
        </div>
      </footer>
    </div>
  );
}
